/**
 * Money.
 *
 * Design 4.1 S4: `amount_minor BIGINT` + `currency currency_code`. **Never a float. Never a
 * single "cents" column without a currency.**
 *
 * The currency is the literal `"EUR"` in TypeScript and the `currency_code` domain in SQL.
 * Product decision "Billing currency — EUR" makes this structural, not a default: EveryPay's
 * `POST /payments` and `POST /notifications` have no `currency` parameter at all — currency
 * is a property of the merchant account. Widening either is the signal that a second
 * processor has actually landed.
 */
import { ValidationError } from "../error/taxonomy.js";
export const CURRENCY_CODE = "EUR";
/** Builds money, rejecting a fractional or unsafe amount rather than rounding it silently. */
export function money(amountMinor, currency) {
    if (!Number.isSafeInteger(amountMinor)) {
        throw new ValidationError({
            code: "validation_failed",
            message: `Money must be an integer number of minor units; received ${String(amountMinor)}`,
            field: "amount_minor",
        });
    }
    return Object.freeze({ amountMinor, currency });
}
/** Adds two amounts of the same currency. Mixing currencies is a type error and a throw. */
export function addMoney(left, right) {
    if (left.currency !== right.currency) {
        throw new ValidationError({
            code: "currency_not_supported",
            message: `Cannot add ${left.currency} to ${right.currency}`,
            field: "currency",
        });
    }
    return money(left.amountMinor + right.amountMinor, left.currency);
}
/** Rounds a rational minor-unit amount to whole minor units, half away from zero. */
export function roundMinor(value, mode) {
    if (mode !== "half_up") {
        throw new ValidationError({
            code: "validation_failed",
            message: `Unsupported rounding mode ${String(mode)}`,
            field: "rounding",
        });
    }
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
//# sourceMappingURL=money.js.map